/**
 * AtsGenerateSection — the "Génération CV adapté" collapsible section of
 * the ATS sidebar. Lets the user pick, per CV section, whether to keep it
 * ("conserver") or rewrite it ("modifier" / "modifier_description") with
 * a chosen adaptation level (fidele / modere / creatif), then triggers
 * the AI generation and displays the result with a minimal diff + an
 * "apply as new version" button.
 *
 * Pure UI component — all state and handlers come from the parent.
 */

import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileEdit,
  Loader2,
} from 'lucide-react';
import type { CVData } from '../../types';
import type { getTType } from '../../contexts/I18nContext';
import type {
  AtsSectionConfig,
  AdaptationLevel,
  AdaptationLevelMap,
} from '../../lib/ai-types';
import type { AtsPreset } from '../../hooks/useCvGeneration';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

type SimpleSectionKey = 'profil' | 'formations' | 'langues' | 'passions' | 'skills';

export interface AtsGenerateSectionProps {
  t: getTType;

  // Section expand/collapse
  isExpanded: boolean;
  onToggleExpanded: () => void;

  // Config (from useCvGeneration)
  atsSectionConfig: AtsSectionConfig;
  setAtsSectionConfig: (updater: (c: AtsSectionConfig) => AtsSectionConfig) => void;
  atsAdaptationLevel: AdaptationLevelMap;
  setAtsAdaptationLevel: (updater: (l: AdaptationLevelMap) => AdaptationLevelMap) => void;
  onApplyPreset: (preset: AtsPreset) => void;

  // Generate state + actions
  isGenerating: boolean;
  generatedCvData: CVData | null;
  generateChangesSummary: string | null;
  generateWarnings: string[] | null;
  onGenerateClick: () => Promise<void> | void;
  onApplyGenerated: () => void;

  // Current CV (for the profile diff)
  data: CVData;

  // Gate
  effectiveJobDescription: string;
}

export function AtsGenerateSection(props: AtsGenerateSectionProps) {
  const {
    t,
    isExpanded,
    onToggleExpanded,
    atsSectionConfig,
    setAtsSectionConfig,
    atsAdaptationLevel,
    setAtsAdaptationLevel,
    onApplyPreset,
    isGenerating,
    generatedCvData,
    generateChangesSummary,
    generateWarnings,
    onGenerateClick,
    onApplyGenerated,
    data,
    effectiveJobDescription,
  } = props;

  const simpleSections: Array<{ sectionKey: SimpleSectionKey; label: string }> = [
    { sectionKey: 'profil', label: t('ats.sectionProfilResume') },
    { sectionKey: 'formations', label: t('sections.formation') },
    { sectionKey: 'langues', label: t('languagesLabel') },
    { sectionKey: 'passions', label: t('hobbies.label') },
    { sectionKey: 'skills', label: t('skills.label') },
  ];

  return (
    <div className="space-y-4">
      <h2
        className="flex items-center justify-between gap-2 text-base font-semibold text-white cursor-pointer hover:text-blue-300 transition-colors border-t border-white/10 pt-4"
        onClick={onToggleExpanded}
      >
        <span className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-400">
            <FileEdit size={18} />
          </div>
          {t('ats.optimizationAts')}
        </span>
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </h2>

      {isExpanded && (
        <div className="bg-gradient-to-br from-emerald-500/5 to-teal-500/5 rounded-xl border border-emerald-500/20 p-4 space-y-4">
          <p className="text-[9px] text-white/50">
            Configurez quelles sections adapter à l'offre puis générez le CV.
          </p>

          {/* Presets */}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => onApplyPreset('conservateur')}
              className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-sm font-bold text-white/90"
            >
              Conservateur
            </button>
            <button
              type="button"
              onClick={() => onApplyPreset('standard')}
              className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-sm font-bold text-white/90"
            >
              Standard
            </button>
            <button
              type="button"
              onClick={() => onApplyPreset('inventif')}
              className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-sm font-bold text-white/90"
            >
              Inventif
            </button>
          </div>

          {/* Per-section config */}
          <div className="space-y-3 max-h-[200px] overflow-y-auto scrollbar-hide pr-1">
            {simpleSections.map(({ sectionKey, label }) => (
              <div
                key={sectionKey}
                className="flex items-center justify-between gap-2 p-2 bg-black/20 rounded-lg"
              >
                <span className="text-[9px] text-white/80">{label}</span>
                <div className="flex gap-2 items-center">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name={`ats-${sectionKey}`}
                      checked={atsSectionConfig[sectionKey] === 'conserver'}
                      onChange={() =>
                        setAtsSectionConfig((c) => ({ ...c, [sectionKey]: 'conserver' }))
                      }
                      className="w-3 h-3"
                    />
                    <span className="text-sm">Conserver</span>
                  </label>
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name={`ats-${sectionKey}`}
                      checked={atsSectionConfig[sectionKey] === 'modifier'}
                      onChange={() =>
                        setAtsSectionConfig((c) => ({ ...c, [sectionKey]: 'modifier' }))
                      }
                      className="w-3 h-3"
                    />
                    <span className="text-sm">Modifier</span>
                  </label>
                  {atsSectionConfig[sectionKey] === 'modifier' && (
                    <Select
                      value={atsAdaptationLevel[sectionKey] ?? 'modere'}
                      onValueChange={(v) =>
                        setAtsAdaptationLevel((l) => ({
                          ...l,
                          [sectionKey]: v as AdaptationLevel,
                        }))
                      }
                    >
                      <SelectTrigger className="h-6 w-[90px] text-sm px-1">
                        <SelectValue placeholder={t('ats.adaptationModere')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fidele" className="text-sm">
                          {t('ats.adaptationFidele')}
                        </SelectItem>
                        <SelectItem value="modere" className="text-sm">
                          {t('ats.adaptationModere')}
                        </SelectItem>
                        <SelectItem value="creatif" className="text-sm">
                          {t('ats.adaptationCreatif')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            ))}

            {/* Experiences: uses modifier_description instead of modifier */}
            <div className="flex items-center justify-between gap-2 p-2 bg-black/20 rounded-lg">
              <span className="text-[9px] text-white/80">{t('sections.experiences')}</span>
              <div className="flex gap-2 items-center">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="ats-experiences"
                    checked={atsSectionConfig.experiences === 'conserver'}
                    onChange={() =>
                      setAtsSectionConfig((c) => ({ ...c, experiences: 'conserver' }))
                    }
                    className="w-3 h-3"
                  />
                  <span className="text-sm">Conserver</span>
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="ats-experiences"
                    checked={atsSectionConfig.experiences === 'modifier_description'}
                    onChange={() =>
                      setAtsSectionConfig((c) => ({
                        ...c,
                        experiences: 'modifier_description',
                      }))
                    }
                    className="w-3 h-3"
                  />
                  <span className="text-sm">Modifier desc.</span>
                </label>
                {atsSectionConfig.experiences === 'modifier_description' && (
                  <Select
                    value={atsAdaptationLevel.experiences ?? 'modere'}
                    onValueChange={(v) =>
                      setAtsAdaptationLevel((l) => ({
                        ...l,
                        experiences: v as AdaptationLevel,
                      }))
                    }
                  >
                    <SelectTrigger className="h-6 w-[90px] text-sm px-1">
                      <SelectValue placeholder={t('ats.adaptationModere')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fidele" className="text-sm">
                        {t('ats.adaptationFidele')}
                      </SelectItem>
                      <SelectItem value="modere" className="text-sm">
                        {t('ats.adaptationModere')}
                      </SelectItem>
                      <SelectItem value="creatif" className="text-sm">
                        {t('ats.adaptationCreatif')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>

          {/* Generate button */}
          <button
            type="button"
            onClick={() => void onGenerateClick()}
            disabled={isGenerating || !effectiveJobDescription?.trim()}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-sm font-bold transition-all disabled:opacity-50"
          >
            {isGenerating ? (
              <>
                <Loader2 size={14} className="animate-spin" /> {t('ats.generating')}
              </>
            ) : (
              <>
                <FileEdit size={14} /> {t('ats.generateAdapted')}
              </>
            )}
          </button>

          {/* Result (generated) */}
          {generatedCvData && (
            <div className="space-y-2 rounded-xl border border-emerald-500/25 bg-black/30 overflow-hidden">
              <div className="px-3 py-2 border-b border-emerald-500/20 bg-emerald-500/10">
                <p className="text-[9px] text-emerald-300 uppercase font-bold flex items-center gap-1.5">
                  <FileEdit size={12} /> {t('ats.adaptedReady')}
                </p>
              </div>
              <div className="p-3 space-y-2">
                {generateChangesSummary && (
                  <p className="text-[9px] text-white/85">{generateChangesSummary}</p>
                )}
                {generateWarnings && generateWarnings.length > 0 && (
                  <ul className="list-disc list-inside text-sm text-amber-400/90">
                    {generateWarnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
                {/* Minimal diff: profile */}
                {atsSectionConfig.profil === 'modifier' &&
                  data.personalInfo.summary !== generatedCvData.personalInfo.summary && (
                    <details className="text-sm">
                      <summary className="cursor-pointer text-green-500">Voir diff Profil</summary>
                      <div className="mt-2 space-y-1 p-2 bg-black/30 rounded">
                        <p className="text-red-400/90">
                          <span className="font-bold">Avant :</span>{' '}
                          {data.personalInfo.summary?.slice(0, 150)}...
                        </p>
                        <p className="text-green-400/90">
                          <span className="font-bold">Après :</span>{' '}
                          {generatedCvData.personalInfo.summary?.slice(0, 150)}...
                        </p>
                      </div>
                    </details>
                  )}
                <button
                  type="button"
                  onClick={onApplyGenerated}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold"
                >
                  <CheckCircle2 size={14} /> {t('ats.createNewVersion')}
                </button>
                <p className="text-sm text-white/50">{t('ats.newVersionNote')}</p>
              </div>
            </div>
          )}

          {/* Error fallback (summary without data) */}
          {generateChangesSummary && !generatedCvData && (
            <div className="p-3 rounded-xl border border-amber-500/25 bg-amber-500/10">
              <p className="text-[9px] text-amber-300 whitespace-pre-wrap">
                {generateChangesSummary}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
